import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Key, Pencil, Trash2, BookOpen } from "lucide-react";

const ConfirmButton = ({ onConfirm, testId, label = "Hiq" }) => {
    const [armed, setArmed] = useState(false);
    return (
        <button type="button" onClick={() => { if (armed) { onConfirm(); setArmed(false); } else { setArmed(true); setTimeout(() => setArmed(false), 3000); } }} data-testid={testId}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold transition ${armed ? "bg-terracotta text-white" : "bg-terracotta/10 text-terracotta hover:bg-terracotta/20"}`}>
            <Trash2 className="w-3 h-3" /> {armed ? "Konfirmo" : label}
        </button>
    );
};

export default function TeachersTab() {
    const [teachers, setTeachers] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [classes, setClasses] = useState([]);
    const [assigns, setAssigns] = useState([]);
    const [open, setOpen] = useState(false);
    const [pwdUser, setPwdUser] = useState(null);
    const [editUser, setEditUser] = useState(null);
    const [editForm, setEditForm] = useState({ name: "", email: "" });
    const [assignFor, setAssignFor] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", password: "teacher123" });
    const [pwdForm, setPwdForm] = useState("");
    const [assignForm, setAssignForm] = useState({ subject_id: "", class_id: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const refresh = async () => {
        const [t, s, c, a] = await Promise.all([
            api.get("/admin/teachers"),
            api.get("/subjects"),
            api.get("/classes"),
            api.get("/admin/teacher-subjects"),
        ]);
        setTeachers(t.data); setSubjects(s.data); setClasses(c.data); setAssigns(a.data);
    };
    useEffect(() => { refresh(); }, []);

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            await api.post("/admin/users", { ...form, role: "teacher" });
            setForm({ name: "", email: "", password: "teacher123" }); setOpen(false); refresh();
        } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
        finally { setBusy(false); }
    };

    const submitPwd = async (e) => {
        e.preventDefault(); setBusy(true);
        try {
            await api.post(`/admin/users/${pwdUser.id}/password`, { new_password: pwdForm });
            setPwdUser(null); setPwdForm("");
            alert("Fjalëkalimi u ndryshua.");
        } catch (e2) { alert("Gabim: " + formatApiError(e2.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const submitEdit = async (e) => {
        e.preventDefault(); setBusy(true);
        try {
            await api.patch(`/admin/users/${editUser.id}`, editForm);
            setEditUser(null); refresh();
        } catch (e2) { alert("Gabim: " + formatApiError(e2.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const deleteTeacher = async (t) => {
        if (!window.confirm(`Fshi mësuesin "${t.name}"? Të dhënat (komente, mungesa, materialet LMS) që ai ka krijuar do të mbeten me emrin e tij.`)) return;
        try { await api.delete(`/admin/users/${t.id}`); refresh(); }
        catch (e2) { alert("Gabim: " + formatApiError(e2.response?.data?.detail)); }
    };

    const submitAssign = async (e) => {
        e.preventDefault(); setBusy(true);
        try {
            await api.post("/admin/teacher-subjects", { teacher_id: assignFor.id, subject_id: assignForm.subject_id, class_id: assignForm.class_id || null });
            setAssignForm({ subject_id: "", class_id: "" });
            refresh();
        } finally { setBusy(false); }
    };

    const removeAssign = async (aid) => { await api.delete(`/admin/teacher-subjects/${aid}`); refresh(); };

    const subjectMap = Object.fromEntries(subjects.map((s) => [s.id, s]));
    const classMap = Object.fromEntries(classes.map((c) => [c.id, c]));

    return (
        <>
            <div className="flex justify-end mb-4">
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button size="sm" className="bg-oxford hover:bg-oxfordhover text-white gap-1.5" data-testid="admin-add-teacher-btn"><Plus className="w-4 h-4" /> Shto Mësues</Button></DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-heading">Krijo Mësues të Ri</DialogTitle></DialogHeader>
                        <form onSubmit={submit} className="space-y-3">
                            <Input placeholder="Emri i plotë" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="admin-add-teacher-name" />
                            <Input type="email" placeholder="Emaili" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="admin-add-teacher-email" />
                            <Input placeholder="Fjalëkalimi" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="admin-add-teacher-password" />
                            {err && <div className="text-xs text-terracotta">{err}</div>}
                            <DialogFooter><Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="admin-add-teacher-submit">{busy ? "Duke krijuar…" : "Krijo"}</Button></DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                {teachers.length === 0 ? <div className="p-8 text-center text-slate-500">Asnjë mësues.</div> : (
                    <div className="divide-y divide-slate-200">
                        {teachers.map((t) => {
                            const myAssigns = assigns.filter((a) => a.teacher_id === t.id);
                            return (
                                <div key={t.id} className="p-4 sm:p-5" data-testid={`teacher-row-${t.id}`}>
                                    <div className="flex items-center gap-2">
                                        <div className="w-10 h-10 rounded-xl bg-oxford/10 text-oxford flex items-center justify-center font-bold text-sm shrink-0">{t.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}</div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-slate-900 truncate">{t.name} {t.role === "super_admin" && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-oxford text-white font-bold">ADMIN</span>}</div>
                                            <div className="text-xs text-slate-500 truncate">{t.email} • {myAssigns.length} lëndë të caktuara</div>
                                        </div>
                                        <button onClick={() => setAssignFor(t)} className="p-1.5 rounded-md text-sage hover:bg-sage/10" title="Cakto lëndë" data-testid={`teacher-assign-${t.id}`}><BookOpen className="w-4 h-4" /></button>
                                        <button onClick={() => { setEditUser(t); setEditForm({ name: t.name, email: t.email }); }} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" title="Edito emër/email" data-testid={`teacher-edit-${t.id}`}><Pencil className="w-4 h-4" /></button>
                                        <button onClick={() => { setPwdUser(t); setPwdForm(""); }} className="p-1.5 rounded-md text-seablue hover:bg-seablue/10" title="Ndrysho fjalëkalimin" data-testid={`teacher-pwd-${t.id}`}><Key className="w-4 h-4" /></button>
                                        {t.role !== "super_admin" && <ConfirmButton onConfirm={() => deleteTeacher(t)} testId={`teacher-delete-${t.id}`} label="Fshi" />}
                                    </div>
                                    {myAssigns.length > 0 && (
                                        <div className="mt-3 pl-13 flex flex-wrap gap-1.5">
                                            {myAssigns.map((a) => {
                                                const subj = subjectMap[a.subject_id];
                                                const klass = a.class_id ? classMap[a.class_id] : null;
                                                return (
                                                    <span key={a.id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-bone border border-slate-200 text-[11px] font-semibold">
                                                        {subj?.name || "?"} {klass ? `• ${klass.name}` : "• të gjitha"}
                                                        <ConfirmButton onConfirm={() => removeAssign(a.id)} testId={`unassign-${a.id}`} />
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <Dialog open={!!pwdUser} onOpenChange={(v) => !v && setPwdUser(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading">Ndrysho fjalëkalimin për {pwdUser?.name}</DialogTitle></DialogHeader>
                    <form onSubmit={submitPwd} className="space-y-3">
                        <Input type="password" placeholder="Fjalëkalimi i ri (min 6)" value={pwdForm} onChange={(e) => setPwdForm(e.target.value)} required minLength={6} data-testid="teacher-pwd-input" />
                        <DialogFooter><Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="teacher-pwd-submit">Ndrysho</Button></DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={!!editUser} onOpenChange={(v) => !v && setEditUser(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading">Edito {editUser?.name}</DialogTitle></DialogHeader>
                    <form onSubmit={submitEdit} className="space-y-3">
                        <Input placeholder="Emri" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required data-testid="teacher-edit-name" />
                        <Input type="email" placeholder="Email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} required data-testid="teacher-edit-email" />
                        <DialogFooter><Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="teacher-edit-submit">Ruaj</Button></DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={!!assignFor} onOpenChange={(v) => !v && setAssignFor(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading">Cakto Lëndë për {assignFor?.name}</DialogTitle></DialogHeader>
                    <form onSubmit={submitAssign} className="space-y-3">
                        <select value={assignForm.subject_id} onChange={(e) => setAssignForm({ ...assignForm, subject_id: e.target.value })} required className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="teacher-assign-subject">
                            <option value="">— Zgjidh lëndën —</option>
                            {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <select value={assignForm.class_id} onChange={(e) => setAssignForm({ ...assignForm, class_id: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="teacher-assign-class">
                            <option value="">— Të gjitha klasat —</option>
                            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <DialogFooter><Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="teacher-assign-submit">Shto</Button></DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}
